import { ContactService } from '../../services/DataService';
import { callAdminApi } from './wixAdminApi';
import { getFirestore, getDocs, getDoc, doc, collection, setDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { getFirebaseApp } from '../firebaseApp';
import {
    Contact,
    ContactFormData,
    ContactAddress,
    ContactListItem,
    ContactEmail,
    ContactPhone,
    ContactResponse,
    CustomField,
    EmailSubscription,
} from '../../types/contacts';
import type { ContactNotes } from '../../types/contacts';
import { ApiError, createApiError, ApiErrorCode } from '../../types/errors';
import { getFullStateName, getFullCountryName, countryCodeMap, stateNameToCode } from '../../utils/locationData';
import { AdminApiResponse } from '../../types/auth';
export { processPhoneWithLibrary, isValidPhone } from '../../utils/phoneUtils';
import { processPhoneWithLibrary } from '../../utils/phoneUtils';

let globalExtendedFieldsCache: {
    data: AdminApiResponse;
    timestamp: number
} | null = null;

const EXTENDED_FIELDS_CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

async function getCachedExtendedFields(): Promise<AdminApiResponse> {
    const now = Date.now();

    if (globalExtendedFieldsCache &&
        (now - globalExtendedFieldsCache.timestamp) < EXTENDED_FIELDS_CACHE_DURATION) {
        return globalExtendedFieldsCache.data;
    }

    const response = await callAdminApi('queryExtendedFields', 'contacts', {
        filter: { namespace: { $eq: 'custom' } },
        limit: 100
    });

    globalExtendedFieldsCache = {
        data: response,
        timestamp: now
    };

    return response;
}

let globalSubscriptionCache: {
    emailMap: Map<string, boolean>;
    smsMap: Map<string, boolean>;
    timestamp: number;
} | null = null;

const GLOBAL_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

function formatPhoneForWix(phone: string): string {
    const processed = processPhoneWithLibrary(phone);
    return processed.e164Phone;
}

async function getSMSSubscriptionStatuses(uniquePhones: string[]): Promise<Map<string, boolean>> {
    try {
        const subscriptionMap = new Map<string, boolean>();

        if (uniquePhones.length === 0) {
            return subscriptionMap;
        }

        try {
            const allContactsResponse = await callAdminApi('queryContacts', 'contacts', {
                limit: 1000
            });

            if (allContactsResponse._items && Array.isArray(allContactsResponse._items)) {
                (allContactsResponse._items as ContactResponse[]).forEach((contact: ContactResponse) => {
                    const contactWithPrimaryPhone = contact as ContactResponse & {
                        primaryPhone?: {
                            phone?: string;
                            subscriptionStatus?: string;
                            e164Phone?: string;
                            formattedPhone?: string;
                        };
                    };

                    if (contactWithPrimaryPhone.primaryPhone?.subscriptionStatus === 'SUBSCRIBED') {
                        const phone = contactWithPrimaryPhone.primaryPhone.phone;
                        if (phone) {
                            subscriptionMap.set(phone, true);
                        }
                    }

                    if (contact.info?.phones) {
                        let phoneItems: ContactPhone[] = [];

                        if (Array.isArray(contact.info.phones)) {
                            phoneItems = contact.info.phones;
                        } else if (typeof contact.info.phones === 'object' && 'items' in contact.info.phones) {
                            const phonesWithItems = contact.info.phones as { items: ContactPhone[] };
                            if (Array.isArray(phonesWithItems.items)) {
                                phoneItems = phonesWithItems.items;
                            }
                        }

                        phoneItems.forEach((phoneItem: ContactPhone) => {
                            const phoneWithSubscription = phoneItem as ContactPhone & {
                                subscriptionStatus?: string;
                            };

                            if (phoneWithSubscription.subscriptionStatus === 'SUBSCRIBED' && phoneItem.phone) {
                                subscriptionMap.set(phoneItem.phone, true);
                            }
                        });
                    }
                });
            }

        } catch (error) {
            throw createApiError(
                ApiErrorCode.CONTACT_FETCH_FAILED,
                `Error checking contact primaryPhone subscription status: ${error instanceof Error ? error.message : String(error)}`
            );
        }

        const finalMap = new Map<string, boolean>();
        uniquePhones.forEach(contactPhone => {
            let isSubscribed = false;

            const processedPhone = processPhoneWithLibrary(contactPhone);

            const phoneFormatsToCheck = [
                contactPhone,
                processedPhone.e164Phone,
                processedPhone.formattedPhone,
                contactPhone.replace(/\D/g, ''),
                '+1' + contactPhone.replace(/\D/g, ''),
            ];

            for (const [subscribedPhone, status] of subscriptionMap.entries()) {
                if (status) {
                    const subscribedProcessed = processPhoneWithLibrary(subscribedPhone);
                    const subscribedFormats = [
                        subscribedPhone,
                        subscribedProcessed.e164Phone,
                        subscribedProcessed.formattedPhone,
                        subscribedPhone.replace(/\D/g, ''),
                    ];

                    const hasMatch = phoneFormatsToCheck.some(contactFormat =>
                        subscribedFormats.some(subFormat => {
                            const contactDigits = contactFormat.replace(/\D/g, '');
                            const subDigits = subFormat.replace(/\D/g, '');
                            return contactDigits === subDigits && contactDigits.length >= 10;
                        })
                    );

                    if (hasMatch) {
                        isSubscribed = true;
                        break;
                    }
                }
            }

            finalMap.set(contactPhone, isSubscribed);
        });

        return finalMap;

    } catch (error) {
        throw createApiError(
            ApiErrorCode.CONTACT_FETCH_FAILED,
            `Error in SMS subscription checking: ${error instanceof Error ? error.message : String(error)}`
        );
    }
}

async function getEmailSubscriptionStatuses(uniqueEmails: string[]): Promise<Map<string, boolean>> {
    try {
        const subscriptionMap = new Map<string, boolean>();

        if (uniqueEmails.length === 0) {
            return subscriptionMap;
        }

        const batchSize = 50;
        let allSubscriptions: EmailSubscription[] = [];

        for (let i = 0; i < uniqueEmails.length; i += batchSize) {
            const emailBatch = uniqueEmails.slice(i, i + batchSize);

            try {
                const response = await callAdminApi('queryEmailSubscriptions', 'emailSubscriptions', {
                    filter: {
                        email: {
                            $in: emailBatch
                        }
                    },
                    options: {
                        paging: {
                            limit: 100,
                            offset: 0
                        }
                    }
                }) as { subscriptions?: EmailSubscription[], metadata?: { total?: number } };

                if (response.subscriptions && Array.isArray(response.subscriptions)) {
                    allSubscriptions = allSubscriptions.concat(response.subscriptions);
                }

                await new Promise(resolve => setTimeout(resolve, 100));

            } catch (error) {
                throw createApiError(
                    ApiErrorCode.CONTACT_FETCH_FAILED,
                    `Error in batch ${Math.floor(i / batchSize) + 1}: ${error instanceof Error ? error.message : String(error)}`
                );
            }
        }

        const uniqueSubscriptions = allSubscriptions.filter((subscription, index, self) => {
            return index === self.findIndex(s => s._id === subscription._id);
        });

        const subscribedEmails = new Set<string>();
        uniqueSubscriptions.forEach((item: EmailSubscription) => {
            if (item.email && item.subscriptionStatus === 'SUBSCRIBED') {
                subscribedEmails.add(String(item.email).toLowerCase());
            }
        });

        uniqueEmails.forEach(email => {
            const isSubscribed = subscribedEmails.has(email.toLowerCase());
            subscriptionMap.set(email, isSubscribed);
        });

        return subscriptionMap;

    } catch (error) {
        throw createApiError(
            ApiErrorCode.CONTACT_FETCH_FAILED,
            `Error checking email subscription statuses: ${error instanceof Error ? error.message : String(error)}`
        );
    }
}

async function getCachedSubscriptionData(emails: string[], phones: string[]): Promise<{
    emailMap: Map<string, boolean>;
    smsMap: Map<string, boolean>;
}> {
    const now = Date.now();

    if (globalSubscriptionCache && (now - globalSubscriptionCache.timestamp) < GLOBAL_CACHE_DURATION) {
        return {
            emailMap: globalSubscriptionCache.emailMap,
            smsMap: globalSubscriptionCache.smsMap
        };
    }

    const [emailMap, smsMap] = await Promise.all([
        getEmailSubscriptionStatuses(emails),
        getSMSSubscriptionStatuses(phones)
    ]);

    const allContactsResponse = await callAdminApi('queryContacts', 'contacts', {
        limit: 1000
    });

    const contactsWithSubscriptionData = new Map<string, string>();

    if (allContactsResponse._items && Array.isArray(allContactsResponse._items)) {
        (allContactsResponse._items as ContactResponse[]).forEach((contact: ContactResponse) => {
            const extendedFields = contact.info?.extendedFields?.items || {};
            const email = contact.info?.emails?.[0]?.email ||
                extendedFields['emailSubscriptions.effectiveEmail'];
            const subscriptionStatus = extendedFields['emailSubscriptions.subscriptionStatus'];

            if (email && subscriptionStatus && subscriptionStatus !== 'NOT_SET') {
                contactsWithSubscriptionData.set(email.toLowerCase(), subscriptionStatus);
            }
        });
    }

    const additionalSubscribers = new Set<string>();
    emails.forEach(email => {
        const normalizedEmail = email.toLowerCase();
        const contactSubscriptionStatus = contactsWithSubscriptionData.get(normalizedEmail);

        if (contactSubscriptionStatus === 'SUBSCRIBED' && !emailMap.get(email)) {
            emailMap.set(email, true);
            additionalSubscribers.add(email);
        }
    });

    globalSubscriptionCache = {
        emailMap,
        smsMap,
        timestamp: now
    };

    return { emailMap, smsMap };
}

function getCountryCodeFromName(countryName: string): string {
    if (!countryName) return 'US';

    if (countryCodeMap[countryName]) {
        return countryCodeMap[countryName];
    }

    const lowerCaseName = countryName.toLowerCase();
    for (const [key, value] of Object.entries(countryCodeMap)) {
        if (key.toLowerCase() === lowerCaseName) {
            return value;
        }
    }

    // Default to US if not found
    return 'US';
}

function getPhoneTagForWix(phoneType: string): string {
    const phoneTypeUpper = phoneType.toUpperCase();

    switch (phoneTypeUpper) {
        case 'WORK':
        case 'HOME':
        case 'MOBILE':
        case 'FAX':
            return phoneTypeUpper;
        default:
            return 'UNTAGGED';
    }
}

function getAddressTagForWix(addressType: string): string {
    const addressTypeUpper = (addressType || '').toUpperCase();
    switch (addressTypeUpper) {
        case 'WORK':
        case 'HOME':
        case 'BILLING':
        case 'SHIPPING':
            return addressTypeUpper;
        default:
            return 'UNTAGGED';
    }
}

function formatSubdivisionForWix(subdivision: string): string {
    if (subdivision.startsWith('US-')) {
        return subdivision.substring(3);
    }

    if (subdivision.startsWith('CA-')) {
        return subdivision.substring(3);
    }

    return subdivision;
}

export class ContactsApi implements ContactService {
    private creatingContacts = new Set<string>(); // Track contacts being created to prevent race conditions

    getFilteredContacts(params: {
        contacts: ContactListItem[];
        searchText: string;
        activeFilter: string;
        selectedLabelId: string;
    }): ContactListItem[] {
        return getFilteredContactsHelper(params);
    }
    constructor() {
        // Reference to avoid TS6133 unused error
        void this.getAllCustomFieldsFallback;
    }

    private generateUniqueId(): string {
        return Date.now().toString() + Math.random().toString(36).slice(2, 11);
    }

    async getAllCustomFields(): Promise<CustomField[]> {
        try {
            const response = await getCachedExtendedFields();
            const customFieldsMap = new Map<string, CustomField>();

            if (response._items && Array.isArray(response._items)) {
                response._items.forEach((item: unknown) => {
                    const extendedField = item as { key?: string; displayName?: string; dataType?: string };
                    const key = extendedField.key;
                    const displayName = extendedField.displayName;
                    const dataType = extendedField.dataType || 'TEXT';
                    const internalAppFields = [
                        'custom.email-tag', 'custom.phone-tag', 'custom.address-tag',
                        'contacts.displayByLastName', 'contacts.displayByFirstName',
                        'invoices.vatId', 'emailSubscriptions.deliverabilityStatus',
                        'emailSubscriptions.subscriptionStatus', 'emailSubscriptions.effectiveEmail',
                        'members.membershipStatus',
                        'custom.last-activity-date-utc-0',
                        'custom.last-activity2',
                        'custom.labels2',
                        'custom.source2',
                        'custom.address-1-type',
                        'custom.created-at-utc-0',
                        'custom.email-subscriber-status',
                        'custom.address-1-zip',
                        'custom.address-1-state-region',
                        'custom.sms-subscriber-status',
                    ];

                    if (key && key.startsWith('custom.') && !internalAppFields.includes(key)) {
                        let fieldType: 'text' | 'number' | 'date' | 'url' = 'text';

                        switch (dataType) {
                            case 'NUMBER':
                                fieldType = 'number';
                                break;
                            case 'DATE':
                                fieldType = 'date';
                                break;
                            case 'URL':
                                fieldType = 'url';
                                break;
                            default:
                                fieldType = 'text';
                        }

                        const label = displayName || key.replace('custom.', '').replace(/-/g, ' ')
                            .replace(/\b\w/g, (l: string) => l.toUpperCase());

                        customFieldsMap.set(key, {
                            id: this.generateUniqueId(),
                            label: label,
                            value: '',
                            type: fieldType,
                            fieldKey: key,
                            dataType: dataType as 'TEXT' | 'NUMBER' | 'DATE' | 'URL'
                        });
                    }
                });
            }

            return Array.from(customFieldsMap.values());
        } catch (error) {
            throw createApiError(
                ApiErrorCode.CONTACT_FETCH_FAILED,
                `Error getting extended fields: ${error instanceof Error ? error.message : String(error)}`
            );
        }
    }



    private async getAllCustomFieldsFallback(): Promise<CustomField[]> {
        try {
            const response = await callAdminApi('queryContacts', 'contacts', {
                limit: 50
            });

            const customFieldsMap = new Map<string, CustomField>();

            if (response._items && Array.isArray(response._items)) {
                (response._items as ContactResponse[]).forEach((contact: ContactResponse) => {
                    const extendedFields = contact.info?.extendedFields?.items || {};

                    Object.entries(extendedFields).forEach(([key]) => {
                        const internalAppFields = [
                            'custom.email-tag', 'custom.phone-tag', 'custom.address-tag',
                            'contacts.displayByLastName', 'contacts.displayByFirstName',
                            'invoices.vatId', 'emailSubscriptions.deliverabilityStatus',
                            'emailSubscriptions.subscriptionStatus', 'emailSubscriptions.effectiveEmail',
                            'members.membershipStatus',
                            'custom.last-activity-date-utc-0',
                            'custom.last-activity2',
                            'custom.labels2',
                            'custom.source2',
                            'custom.address-1-type',
                            'custom.created-at-utc-0',
                            'custom.email-subscriber-status',
                            'custom.address-1-zip',
                            'custom.address-1-state-region',
                            'custom.sms-subscriber-status',
                        ];

                        if (key && key.startsWith('custom.') && !internalAppFields.includes(key)) {
                            const label = key.replace('custom.', '').replace(/-/g, ' ')
                                .replace(/\b\w/g, (l: string) => l.toUpperCase());

                            customFieldsMap.set(key, {
                                id: this.generateUniqueId(),
                                label: label,
                                value: '',
                                type: 'text',
                                fieldKey: key
                            });
                        }
                    });
                });
            }

            return Array.from(customFieldsMap.values());
        } catch (error) {
            throw createApiError(
                ApiErrorCode.CONTACT_FETCH_FAILED,
                `Error in fallback method: ${error instanceof Error ? error.message : String(error)}`
            );
        }
    }

    async getContactDetails(id: string): Promise<Contact | null> {
        try {
            const response = await callAdminApi('queryContacts', 'contacts', {
                filter: {
                    _id: { $eq: id }
                },
                limit: 1
            });

            if (!response._items || response._items.length === 0) {
                throw createApiError(
                    ApiErrorCode.CONTACT_NOT_FOUND,
                    `No contact found with ID: ${id}`
                );
            }

            const contact = response._items[0] as ContactResponse & {
                _id?: string;
            };

            if (!contact) return null;

            const info = contact.info || {} as {
                extendedFields?: {
                    items?: Record<string, string>;
                };
                customFields?: Record<string, unknown>;
                jobTitle?: string;
            };

            const customFieldsArray: CustomField[] = [];

            if (info.extendedFields?.items) {
                Object.entries(info.extendedFields.items).forEach(async ([key, value]) => {
                    if (key.startsWith('custom.') &&
                        value &&
                        typeof value === 'string' &&
                        value.trim() &&
                        !key.includes('notes') &&
                        !key.includes('time-and-date') &&
                        !key.includes('email-tag') &&
                        !key.includes('phone-tag') &&
                        !key.includes('address-tag') &&
                        !key.includes('last-activity') &&
                        !key.includes('labels') &&
                        !key.includes('displayByLastName')) {

                        const fieldLabel = await this.getAllCustomFieldsForLabel(key);

                        customFieldsArray.push({
                            id: this.generateUniqueId(),
                            label: fieldLabel,
                            value: String(value),
                            type: 'personal',
                            fieldKey: key
                        });
                    }
                });
            }

            if (info.customFields) {
                Object.entries(info.customFields).forEach(([key, value]) => {
                    if (value && String(value).trim()) {
                        customFieldsArray.push({
                            id: this.generateUniqueId(),
                            label: key,
                            value: String(value),
                            type: 'personal',
                            fieldKey: key
                        });
                    }
                });
            }

            const contactWithMemberInfo = contact as ContactResponse & {
                memberInfo?: { status?: 'ACTIVE' | 'PENDING' | 'BLOCKED' | 'INACTIVE' | string }
            };

            const memberStatus = contactWithMemberInfo.memberInfo?.status || null;

            const contactDetails: Contact = {
                _id: contact._id || '',
                id: contact._id || '',
                firstName: '',
                lastName: '',
                company: '',
                position: '',
                avatar: '',
                source: 'wix',
                createdAt: '',
                updatedAt: '',
                emails: [],
                phones: [],
                addresses: [],
                address: '', // Added to satisfy Contact type
                addressLine1: '', // Added to satisfy Contact type
                customFields: customFieldsArray,
                labels: [],
                notes: [],
                isMember: memberStatus === 'ACTIVE' || memberStatus === 'PENDING',
                memberStatus: memberStatus,
                memberInfo: memberStatus ? { status: memberStatus } : undefined,
            };

            return contactDetails;

        } catch (error) {
            if (error && typeof error === 'object' && 'code' in error && 'message' in error) {
                throw error as ApiError;
            }

            throw createApiError(
                ApiErrorCode.CONTACT_FETCH_FAILED,
                error instanceof Error ? error.message : 'Failed to fetch contact'
            );
        }
    }

    // ── Native Wix Notes API ──────────────────────────────────────────

    // Wix dates come back through Firebase serialization in several forms:
    // - ISO string, e.g. "2024-01-15T10:30:00.000Z"
    // - Firebase Timestamp object: { _seconds: N, _nanoseconds: N }
    // - Plain seconds object: { seconds: N }
    // - Native Date (unlikely post-serialization, but handled for safety)
    _parseNoteDate(raw: unknown): string {
        if (!raw) return new Date().toISOString();
        if (typeof raw === 'string') {
            const d = new Date(raw);
            return isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
        }
        if (raw instanceof Date) return raw.toISOString();
        if (typeof raw === 'object') {
            const t = raw as Record<string, unknown>;
            const secs = (t['_seconds'] ?? t['seconds']) as number | undefined;
            if (typeof secs === 'number') return new Date(secs * 1000).toISOString();
        }
        return new Date().toISOString();
    }

    async listContactNotes(contactId: string): Promise<ContactNotes[]> {
        try {
            const result = await callAdminApi('listNotes', 'contacts', { contactId }) as {
                notes?: Array<{ _id?: string; text?: string; _createdDate?: unknown; _updatedDate?: unknown; revision?: string }>;
            };
            return (result.notes || []).map(n => ({
                id: n._id || '',
                content: n.text || '',
                createdAt: this._parseNoteDate(n._createdDate),
                updatedAt: this._parseNoteDate(n._updatedDate),
                revision: n.revision,
            }));
        } catch (error) {
            throw createApiError(
                ApiErrorCode.CONTACT_FETCH_FAILED,
                `Error listing notes for contact ${contactId}: ${error instanceof Error ? error.message : String(error)}`
            );
        }
    }

    async createContactNote(contactId: string, content: string): Promise<ContactNotes> {
        try {
            const result = await callAdminApi('createNote', 'contacts', { contactId, content }) as {
                _id?: string; text?: string; _createdDate?: unknown; _updatedDate?: unknown; revision?: string;
            };
            return {
                id: result._id || '',
                content: result.text || content,
                createdAt: this._parseNoteDate(result._createdDate),
                updatedAt: this._parseNoteDate(result._updatedDate),
                revision: result.revision,
            };
        } catch (error) {
            throw createApiError(
                ApiErrorCode.CONTACT_UPDATE_FAILED,
                `Error creating note for contact ${contactId}: ${error instanceof Error ? error.message : String(error)}`
            );
        }
    }

    async updateContactNote(contactId: string, noteId: string, content: string, revision: string): Promise<ContactNotes> {
        try {
            const result = await callAdminApi('updateNote', 'contacts', { noteId, revision, content }) as {
                _id?: string; text?: string; _createdDate?: unknown; _updatedDate?: unknown; revision?: string;
            };
            return {
                id: result._id || noteId,
                content: result.text || content,
                createdAt: this._parseNoteDate(result._createdDate),
                updatedAt: this._parseNoteDate(result._updatedDate),
                revision: result.revision,
            };
        } catch (error) {
            throw createApiError(
                ApiErrorCode.CONTACT_UPDATE_FAILED,
                `Error updating note ${noteId} for contact ${contactId}: ${error instanceof Error ? error.message : String(error)}`
            );
        }
    }

    async deleteContactNote(contactId: string, noteId: string): Promise<boolean> {
        try {
            await callAdminApi('deleteNote', 'contacts', { noteId });
            return true;
        } catch (error) {
            throw createApiError(
                ApiErrorCode.CONTACT_UPDATE_FAILED,
                `Error deleting note ${noteId} for contact ${contactId}: ${error instanceof Error ? error.message : String(error)}`
            );
        }
    }

    async getAllContacts(): Promise<ContactListItem[]> {
        const t0 = performance.now();
        try {
            const db = getFirestore(getFirebaseApp(), 'contacts');
            const snap = await getDocs(collection(db, 'contacts'));
            if (!snap.empty) {
                console.log(`[contacts.getAllContacts] Firestore hit: ${snap.size} in ${(performance.now()-t0).toFixed(0)}ms`);
                return snap.docs.map(d => {
                    const data = d.data();
                    const firstName = data.firstName || '';
                    const lastName = data.lastName || '';
                    return {
                        id: d.id,
                        _id: d.id,
                        firstName,
                        lastName,
                        displayName: data.name || `${firstName} ${lastName}`.trim() || (data.email ? String(data.email).split('@')[0] : ''),
                        primaryEmail: data.email || '',
                        primaryPhone: data.phone || '',
                        company: data.company,
                        avatar: data.avatar || '',
                        labels: Array.isArray(data.labels) ? data.labels : [],
                        labelIds: Array.isArray(data.labels) ? data.labels : [],
                        emailSubscribed: data.emailSubscribed ?? false,
                        smsSubscribed: data.smsSubscribed ?? true,
                        zohoContactId: (data.zohoContactId as string) || undefined,
                        customerCode: (data.customerCode as string) || undefined,
                    } as ContactListItem;
                });
            }
            console.log(`[contacts.getAllContacts] Firestore empty after ${(performance.now()-t0).toFixed(0)}ms — falling to Wix`);
        } catch (e) {
            console.warn(`[contacts.getAllContacts] Firestore error after ${(performance.now()-t0).toFixed(0)}ms — falling to Wix:`, e);
        }

        console.log(`[contacts.getAllContacts] Starting Wix API fetch at ${(performance.now()-t0).toFixed(0)}ms`);
        try {
            const firstResponse = await callAdminApi('queryContacts', 'contacts', {
                skip: 0,
                limit: 100
            });

            let allContacts = firstResponse._items || [];
            if (allContacts.length === 100) {
                let skip = 100;
                let hasMore = true;

                while (hasMore && skip < 500) {
                    try {
                        const response = await callAdminApi('queryContacts', 'contacts', {
                            skip: skip,
                            limit: 100
                        });

                        const items = response._items || [];

                        if (items.length === 0) {
                            hasMore = false;
                            break;
                        }

                        allContacts = allContacts.concat(items);
                        skip += 100;

                        if (items.length < 100) {
                            hasMore = false;
                        }

                        await new Promise(resolve => setTimeout(resolve, 100));

                    } catch (error) {
                        throw createApiError(
                            ApiErrorCode.CONTACT_FETCH_FAILED,
                            `Error fetching contacts batch: ${error instanceof Error ? error.message : String(error)}`
                        );
                    }
                }
            }

            // Remove duplicates by ID
            const uniqueContacts = (allContacts as ContactResponse[]).filter((contact, index, self) =>
                index === self.findIndex((c) => c._id === contact._id)
            );

            return this.mapContactsToList(uniqueContacts);

        } catch (error) {
            throw createApiError(
                ApiErrorCode.CONTACT_FETCH_FAILED,
                `Error in getAllContacts: ${error instanceof Error ? error.message : String(error)}`
            );
        }
    }

    private mapContactsToList(contacts: ContactResponse[]): ContactListItem[] {
        const uniqueContacts = contacts.filter((contact, index, self) =>
            index === self.findIndex(c => c._id === contact._id)
        );

        const mappedContacts: ContactListItem[] = uniqueContacts.map((contact: ContactResponse) => {
            const info = contact.info || {} as {
                name?: { first?: string; last?: string };
                emails?: ContactEmail[];
                phones?: ContactPhone[];
                addresses?: ContactResponse[];
                company?: string;
                position?: string;
                jobTitle?: string;
                customFields?: Record<string, unknown>;
                createdDate?: string;
                updatedDate?: string;
            };
            const name = info.name || {};

            let emails = Array.isArray(info.emails) ? info.emails : [];
            let phones = Array.isArray(info.phones) ? info.phones : [];

            let firstEmail = emails.length > 0 ? emails[0] : {};
            let firstPhone = phones.length > 0 ? phones[0] : {};
            let labelIds: string[] = [];

            if (
                'labelKeys' in info &&
                info['labelKeys'] &&
                typeof info['labelKeys'] === 'object' &&
                info['labelKeys'] !== null &&
                Array.isArray(((info['labelKeys'] as Record<string, unknown>) as { items?: unknown }).items)
            ) {
                labelIds = ((info['labelKeys'] as Record<string, unknown>) as { items: string[] }).items;
            }

            if (!('email' in firstEmail) || !firstEmail.email) {
                const primaryInfo = (contact as Record<string, unknown>).primaryInfo as Record<string, unknown> || {};
                if (primaryInfo && 'email' in primaryInfo && primaryInfo.email) {
                    firstEmail = { id: '', email: primaryInfo.email as string };
                }
            }

            if (!('phone' in firstPhone) || !firstPhone.phone) {
                const primaryInfo = (contact as Record<string, unknown>).primaryInfo as Record<string, unknown> || {};
                if (primaryInfo && 'phone' in primaryInfo && primaryInfo.phone) {
                    firstPhone = { id: '', phone: primaryInfo.phone as string };
                }
            }

            let avatarUrl = '';
            const contactWithPicture = contact as Record<string, unknown>;
            if (contactWithPicture.picture) {
                if (typeof contactWithPicture.picture === 'object' && contactWithPicture.picture !== null) {
                    const pictureObj = contactWithPicture.picture as Record<string, unknown>;
                    if (pictureObj.image) {
                        avatarUrl = String(pictureObj.image);
                    } else if (pictureObj.url) {
                        avatarUrl = String(pictureObj.url);
                    }
                } else if (typeof contactWithPicture.picture === 'string') {
                    avatarUrl = contactWithPicture.picture as string;
                }
            } else if (info && 'picture' in info) {
                const infoPicture = (info as Record<string, unknown>).picture;
                if (typeof infoPicture === 'object' && infoPicture !== null) {
                    const pictureObj = infoPicture as Record<string, unknown>;
                    if (pictureObj.image) {
                        avatarUrl = String(pictureObj.image);
                    } else if (pictureObj.url) {
                        avatarUrl = String(pictureObj.url);
                    }
                } else if (typeof infoPicture === 'string') {
                    avatarUrl = infoPicture as string;
                }
            }

            let clientTypes: string[] = ['contacts'];
            let clientStatus = 'active';

            const contactWithMemberInfo = contact as { memberInfo?: { status?: string } };
            if (contactWithMemberInfo.memberInfo && typeof contactWithMemberInfo.memberInfo === 'object') {
                const memberStatus = contactWithMemberInfo.memberInfo.status;
                if (typeof memberStatus === 'string') {
                    switch (memberStatus.toUpperCase()) {
                        case 'APPROVED':
                        case 'ACTIVE':
                            clientTypes = ['members'];
                            clientStatus = 'active';
                            break;
                        case 'PENDING':
                            clientTypes = ['pending members'];
                            clientStatus = 'pending';
                            break;
                        case 'BLOCKED':
                            clientTypes = ['blocked members'];
                            clientStatus = 'inactive';
                            break;
                    }
                }
            }

            return {
                id: String(contact._id || ''),
                _id: String(contact._id || ''),
                firstName: String(name.first || ''),
                lastName: String(name.last || ''),
                displayName: (name.first || name.last) ?
                    `${String(name.first || '')} ${String(name.last || '')}`.trim() :
                    'email' in firstEmail && firstEmail.email ? String(firstEmail.email).split('@')[0] : '',
                primaryEmail: String('email' in firstEmail ? firstEmail.email : ''),
                primaryPhone: String(firstPhone && 'phone' in firstPhone ? firstPhone.phone : ''),
                createdAt: String((contact as { _createdDate?: string })._createdDate || ''),
                _createdDate: String((contact as { _createdDate?: string })._createdDate || ''),
                avatar: avatarUrl,
                imageUrl: avatarUrl,
                labelIds: [],
                labels: labelIds,
                contactsCount: 0,
                status: clientStatus,
                contactfilter: [{
                    searchText: '',
                    filterType: 'all',
                    clientType: clientTypes,
                    clientStatus: clientStatus
                }]
            };
        });

        return mappedContacts;
    }

    // Firestore is the primary read path for a single contact — Wix Admin API is fallback
    // only, matching the pattern already used by getAllContacts(). Firestore's contacts/{id}
    // doc is a reduced sync-bridge schema (see functions/src/http/onWixContactCreated.ts), so
    // custom fields, birthdate, jobTitle/position, and member status aren't available this way —
    // those stay empty/undefined on the Firestore path. Editing/saving still goes through Wix
    // (updateContact() re-fetches live Wix data at save-time regardless of how the page loaded).
    async getContactById(id: string, preserveOrder?: CustomField[]): Promise<Contact | null> {
        try {
            const db = getFirestore(getFirebaseApp(), 'contacts');
            const snap = await getDoc(doc(db, 'contacts', id));
            if (snap.exists()) {
                return await this.mapFirestoreContact(id, snap.data());
            }
        } catch (e) {
            console.warn(`[contacts.getContactById] Firestore error for ${id} — falling back to Wix:`, e);
        }

        return this.getContactByIdFromWix(id, preserveOrder);
    }

    // Live listener on the Firestore contacts/{id} doc — fires with the mapped
    // Contact on every write (this app, Zoho sync, Helcim sync, contactSyncTrigger).
    // Returns an unsubscribe fn. Never falls back to Wix — used for real-time
    // freshness on top of an initial getContactById() load.
    subscribeToContact(
        id: string,
        onChange: (contact: Contact) => void,
        onError?: (e: unknown) => void,
    ): () => void {
        try {
            const db = getFirestore(getFirebaseApp(), 'contacts');
            return onSnapshot(
                doc(db, 'contacts', id),
                async (snap) => {
                    if (!snap.exists()) return;
                    try {
                        onChange(await this.mapFirestoreContact(id, snap.data()));
                    } catch (e) {
                        onError?.(e);
                    }
                },
                (e) => onError?.(e),
            );
        } catch (e) {
            onError?.(e);
            return () => { /* no-op */ };
        }
    }

    // Firestore stores raw canonical Wix tags (MOBILE/WORK/HOME/FAX/BILLING/SHIPPING/UNTAGGED) —
    // kept raw so contactSyncTrigger's cross-system diffing stays stable (see functions/src/
    // triggers/contactSyncTrigger.ts and functions/src/http/onWixContactCreated.ts). Prettifying
    // for display happens here, read-time only, so it can never affect what gets synced/diffed.
    private prettifyTag(rawTag: string | undefined, fallback: string): string {
        if (!rawTag || rawTag === 'UNTAGGED') return fallback;
        return rawTag.charAt(0).toUpperCase() + rawTag.slice(1).toLowerCase();
    }

    private async mapFirestoreContact(id: string, data: Record<string, any>): Promise<Contact> {
        const rawCustomFields = (data.customFields ?? {}) as Record<string, string>;
        let customFields: CustomField[] = [];
        if (Object.keys(rawCustomFields).length > 0) {
            try {
                const fieldDefs = await this.getAllCustomFields();
                const labelByKey = new Map(fieldDefs.map(f => [f.fieldKey, f.label]));
                customFields = Object.entries(rawCustomFields).map(([key, value]) => ({
                    id: key,
                    label: labelByKey.get(key) || key.replace('custom.', '').replace(/-/g, ' ')
                        .split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
                    value: value ? String(value).trim() : '',
                    type: 'text',
                    fieldKey: key,
                }));
            } catch (e) {
                console.warn(`[contacts.mapFirestoreContact] Failed to resolve custom field labels for ${id}:`, e);
            }
        }

        const firstName = data.firstName || '';
        const lastName = data.lastName || '';

        // IDs are content-based (not array position) so they stay stable across reorders
        // (e.g. "Make Primary" moving an item to index 0) and across a reload landing
        // mid-edit — a position-based id like `phone-${index}` can end up reused by a
        // different logical item once the array order changes, which is exactly the kind
        // of thing that causes React list-reconciliation bugs and stale form state.
        // A contact can genuinely have the same number/address entered twice though (e.g.
        // a duplicate synced from Wix) — content-based ids alone collide in that case, so
        // repeats within this one contact get a disambiguating suffix.
        const seenPhoneIds = new Map<string, number>();
        const phones: ContactPhone[] = Array.isArray(data.phones)
            ? data.phones.map((p: { tag?: string; phone?: string; primary?: boolean }, index: number) => {
                const processed = processPhoneWithLibrary(p.phone || '');
                const baseId = p.phone ? `phone-${p.phone}` : `phone-${index}-${this.generateUniqueId()}`;
                const occurrence = seenPhoneIds.get(baseId) || 0;
                seenPhoneIds.set(baseId, occurrence + 1);
                return {
                    id: occurrence > 0 ? `${baseId}-${occurrence}` : baseId,
                    phone: processed.phone,
                    tag: this.prettifyTag(p.tag, 'Mobile'),
                    primary: Boolean(p.primary),
                    formattedPhone: processed.formattedPhone,
                    e164Phone: processed.e164Phone,
                    countryCode: processed.countryCode,
                };
            })
            : [];

        const seenEmailIds = new Map<string, number>();
        const emails: ContactEmail[] = Array.isArray(data.emails)
            ? data.emails.map((e: { tag?: string; email?: string; primary?: boolean }, index: number) => {
                const baseId = e.email ? `email-${e.email}` : `email-${index}-${this.generateUniqueId()}`;
                const occurrence = seenEmailIds.get(baseId) || 0;
                seenEmailIds.set(baseId, occurrence + 1);
                return {
                    id: occurrence > 0 ? `${baseId}-${occurrence}` : baseId,
                    email: e.email || '',
                    tag: this.prettifyTag(e.tag, 'Personal'),
                    primary: Boolean(e.primary),
                };
            })
            : [];

        const addresses: ContactAddress[] = Array.isArray(data.addresses)
            ? data.addresses.map((a: { tag?: string; primary?: boolean; street?: string; street2?: string; city?: string; state?: string; zip?: string; country?: string }, index: number) => ({
                id: a.street ? `address-${a.street}-${index}` : `address-${index}-${this.generateUniqueId()}`,
                address: {
                    addressLine1: a.street || '',
                    addressLine2: a.street2 || '',
                    city: a.city || '',
                    subdivision: a.state ? getFullStateName(a.state) : '',
                    state: a.state ? getFullStateName(a.state) : '',
                    postalCode: a.zip || '',
                    country: a.country ? getFullCountryName(a.country) : '',
                },
                tag: this.prettifyTag(a.tag, 'Home'),
                primary: Boolean(a.primary),
            }))
            : [];

        return {
            id,
            _id: id,
            firstName,
            lastName,
            avatar: data.avatar || '',
            company: data.company || '',
            position: data.jobTitle || '',
            jobTitle: data.jobTitle || '',
            birthdate: data.birthdate || undefined,
            address: '',
            addressLine1: '',
            phones,
            emails,
            addresses,
            customFields,
            source: 'firestore',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            labels: Array.isArray(data.labels) ? data.labels : [],
            isMember: Boolean(data.isMember),
            memberStatus: data.memberStatus || null,
            memberInfo: data.memberStatus ? { status: data.memberStatus } : undefined,
            wixContactId: data.wixContactId || id,
            zohoContactId: data.zohoContactId || undefined,
            helcimCustomerCode: data.customerCode || undefined,
            displayNameSource: data.displayNameSource || undefined,
        };
    }

    // Direct Firestore write, bypassing Wix entirely — Wix has no equivalent field, and
    // this choice only matters to Zoho. syncSource: 'app' lets contactSyncTrigger fan out
    // normally (it isn't 'wix'/'zoho'/'helcim'/'trigger', so nothing gets skipped).
    async updateDisplayNameSource(id: string, value: 'company' | 'name' | ''): Promise<void> {
        const db = getFirestore(getFirebaseApp(), 'contacts');
        await setDoc(doc(db, 'contacts', id), {
            displayNameSource: value,
            syncSource: 'app',
            updatedAt: serverTimestamp(),
        }, { merge: true });
    }

    private async getContactByIdFromWix(id: string, preserveOrder?: CustomField[]): Promise<Contact | null> {
        try {
            const response = await callAdminApi('queryContacts', 'contacts', {
                filter: {
                    _id: { $eq: id }
                },
                limit: 1
            });

            if (!response._items || response._items.length === 0) {
                throw createApiError(
                    ApiErrorCode.CONTACT_NOT_FOUND,
                    `No contact found with ID: ${id}`
                );
            }


            const contact = response._items[0] as ContactResponse & {
                _id?: string;
                _createdDate?: string;
                _updatedDate?: string;
                revision?: string;
                picture?: string;
            };


            if (contact._id !== id) {
                throw createApiError(
                    ApiErrorCode.CONTACT_NOT_FOUND,
                    `ID mismatch! Requested: ${id}, Got: ${contact._id}`
                );
            }

            const info = contact.info || {} as {
                name?: { first?: string; last?: string };
                emails?: ContactEmail[];
                phones?: ContactPhone[];
                addresses?: ContactAddress[];
                company?: string;
                position?: string;
                jobTitle?: string;
                birthdate?: string;
                customFields?: Record<string, unknown>;
                extendedFields?: { items?: Record<string, string> };
            };
            const name = info.name || {};

            let phones = (info.phones && typeof info.phones === 'object' && 'items' in info.phones && Array.isArray(info.phones.items))
                ? info.phones.items as ContactPhone[]
                : [];
            let emails = (info.emails && typeof info.emails === 'object' && 'items' in info.emails && Array.isArray(info.emails.items))
                ? info.emails.items as ContactEmail[]
                : [];
            let addresses = (info.addresses && typeof info.addresses === 'object' && 'items' in info.addresses && Array.isArray(info.addresses.items))
                ? info.addresses.items as ContactAddress[]
                : [];

            let customEmailTags: string[] = [];
            let customPhoneTags: string[] = [];
            let customAddressTags: string[] = [];

            if (info.extendedFields?.items) {
                const emailTagString = info.extendedFields.items['custom.email-tag'] || '';
                if (emailTagString) {
                    customEmailTags = emailTagString.split(', ').map(tag => tag.trim()).filter(tag => tag);
                }

                const phoneTagString = info.extendedFields.items['custom.phone-tag'] || '';
                if (phoneTagString) {
                    customPhoneTags = phoneTagString.split(', ').map(tag => tag.trim()).filter(tag => tag);
                }

                const addressTagString = info.extendedFields.items['custom.address-tag'] || '';
                if (addressTagString) {
                    customAddressTags = addressTagString.split(', ').map(tag => tag.trim()).filter(tag => tag);
                }
            }

            const contactWithMemberInfo = contact as ContactResponse & {
                memberInfo?: { status?: 'ACTIVE' | 'PENDING' | 'BLOCKED' | 'INACTIVE' | string }
            };

            const memberStatus = contactWithMemberInfo.memberInfo?.status || null;

            let contactLabels: string[] = [];

            if (info && 'labelKeys' in info &&
                info.labelKeys &&
                typeof info.labelKeys === 'object' &&
                info.labelKeys !== null) {

                const labelKeysObj = info.labelKeys as { items?: string[] };
                if (Array.isArray(labelKeysObj.items)) {
                    contactLabels = labelKeysObj.items;
                }
            }

            if ('labelKeys' in contact &&
                contact.labelKeys &&
                typeof contact.labelKeys === 'object' &&
                contact.labelKeys !== null) {

                const labelKeysObj = contact.labelKeys as { items?: string[] };
                if (Array.isArray(labelKeysObj.items)) {
                    contactLabels = labelKeysObj.items;
                }
            }

            // Extract avatar URL - simplified for safety
            const avatarUrl = typeof contact.picture === 'string' ? contact.picture : '';

            // Debug: log mapping values immediately before assignment

            // Extract company, position, jobTitle, and birthdate from info object
            let company = info.company || '';
            let jobTitle = info.jobTitle || '';
            let position = info.jobTitle || '';
            let birthdate = info.birthdate || '';

          

         

            const contactItem: Contact = {
                id: contact._id || '',
                _id: contact._id || '',
                firstName: name.first || '',
                lastName: name.last || '',
                avatar: avatarUrl,
                company: company,
                position: position,
                jobTitle: jobTitle,
                birthdate: birthdate || undefined,
                address: '',
                addressLine1: '',

                phones: phones.map((p: ContactPhone, index) => {
                    const processedPhone = processPhoneWithLibrary(p.phone || '');

                    let displayTag = 'Mobile';
                    const wixPhoneTags = ["WORK", "HOME", "MOBILE", "FAX"];

                    if (p.tag && p.tag !== 'UNTAGGED') {
                        const phoneTypeUpper = p.tag.toUpperCase();

                        if (wixPhoneTags.includes(phoneTypeUpper)) {
                            displayTag = p.tag.charAt(0).toUpperCase() + p.tag.slice(1).toLowerCase();
                        } else {
                            const customTagIndex = phones.slice(0, index).filter(phone => {
                                const phoneUpper = phone.tag?.toUpperCase() || '';
                                return phone.tag === 'UNTAGGED' || (!wixPhoneTags.includes(phoneUpper) && phone.tag?.toLowerCase() !== 'other');
                            }).length;

                            if (customPhoneTags.length > 0 && customTagIndex < customPhoneTags.length) {
                                displayTag = customPhoneTags[customTagIndex];
                            }
                        }
                    } else if (p.tag === 'UNTAGGED') {
                        const untaggedPhoneIndex = phones.slice(0, index + 1).filter(phone => phone.tag === 'UNTAGGED').length - 1;

                        if (customPhoneTags.length > 0 && untaggedPhoneIndex < customPhoneTags.length) {
                            displayTag = customPhoneTags[untaggedPhoneIndex];
                        }
                    }

                    return {
                        id: p._id || this.generateUniqueId(),
                        _id: p._id,
                        phone: processedPhone.phone,
                        tag: displayTag,
                        primary: Boolean(p.primary) || false,
                        formattedPhone: processedPhone.formattedPhone,
                        e164Phone: processedPhone.e164Phone,
                        countryCode: processedPhone.countryCode
                    } satisfies ContactPhone;
                }),

                emails: emails.map((e: ContactEmail, index) => {
                    let displayTag = 'Personal';

                    if (customEmailTags.length > 0 && index < customEmailTags.length) {
                        displayTag = customEmailTags[index];
                    } else if (e.tag && e.tag !== 'UNTAGGED') {
                        displayTag = e.tag.charAt(0).toUpperCase() + e.tag.slice(1).toLowerCase();
                    }

                    return {
                        id: e._id || this.generateUniqueId(),
                        email: e.email || '',
                        tag: displayTag,
                        isPrimary: Boolean(e.primary) || false
                    };
                }),

                addresses: addresses.map((a: ContactAddress & { address?: Record<string, unknown>; tag?: string; primary?: boolean; _id?: string }, index) => {
                    const address = a.address || {};
                    const streetAddr = typeof address.addressLine === 'object' && address.addressLine !== null
                        ? address.addressLine as { number?: string; name?: string; apt?: string }
                        : {};

                    let currentAddressLine1 = '';
                    let currentAddressLine2 = '';
                    if ('addressLine1' in address) {
                        currentAddressLine1 = String(address.addressLine1 || '');
                        currentAddressLine2 = String(address.addressLine2 || '');
                    } else {
                        currentAddressLine1 = `${streetAddr.number || ''} ${streetAddr.name || ''}`.trim();
                        currentAddressLine2 = streetAddr.apt || '';
                    }

                    const subdivisionValue = typeof address.subdivision === 'string' ? address.subdivision : '';

                    let fullStateName = subdivisionValue;

                    if (stateNameToCode[subdivisionValue]) {
                        fullStateName = subdivisionValue;
                    } else {
                        fullStateName = getFullStateName(subdivisionValue);
                    }

                    let displayTag = 'Home';
                    const wixAddressTags = ["WORK", "HOME", "BILLING", "SHIPPING"];
                    if (a.tag && a.tag !== 'UNTAGGED') {
                        const addressTypeUpper = a.tag.toUpperCase();
                        if (wixAddressTags.includes(addressTypeUpper)) {
                            displayTag = a.tag.charAt(0).toUpperCase() + a.tag.slice(1).toLowerCase();
                        } else {
                            displayTag = a.tag;
                        }
                    } else if (a.tag === 'UNTAGGED') {
                        const untaggedAddressIndex = addresses.slice(0, index + 1).filter(addr => addr.tag === 'UNTAGGED').length - 1;
                        if (customAddressTags.length > 0 && untaggedAddressIndex < customAddressTags.length) {
                            displayTag = customAddressTags[untaggedAddressIndex];
                        } else {
                            displayTag = 'Other';
                        }
                    }

                    return {
                        id: a._id || this.generateUniqueId(),
                        address: {
                            addressLine1: currentAddressLine1,
                            addressLine2: currentAddressLine2,
                            city: typeof address.city === 'string' ? address.city : '',
                            state: fullStateName,
                            subdivision: fullStateName,
                            postalCode: typeof address.postalCode === 'string' ? address.postalCode : '',
                            country: typeof address.country === 'string' ? getFullCountryName(address.country) : ''
                        },
                        tag: displayTag,
                        isPrimary: Boolean(a.primary) || false
                    };
                }),

                customFields: await (async () => {
                    const customFields: CustomField[] = [];

                    if (info.extendedFields?.items) {
                        const internalAppFields = [
                            'custom.email-tag', 'custom.phone-tag', 'custom.address-tag',
                            'contacts.displayByLastName', 'contacts.displayByFirstName',
                            'invoices.vatId', 'emailSubscriptions.deliverabilityStatus',
                            'emailSubscriptions.subscriptionStatus', 'emailSubscriptions.effectiveEmail',
                            'members.membershipStatus',
                            'custom.last-activity-date-utc-0',
                            'custom.last-activity2',
                            'custom.labels2',
                            'custom.source2',
                            'custom.address-1-type',
                            'custom.created-at-utc-0',
                            'custom.email-subscriber-status',
                            'custom.address-1-zip',
                            'custom.address-1-state-region',
                            'custom.sms-subscriber-status',
                        ];

                        const allCustomFields = await this.getAllCustomFields();
                        const fieldDisplayNames = new Map<string, string>();
                        allCustomFields.forEach(field => {
                            fieldDisplayNames.set(field.fieldKey, field.label);
                        });

                        for (const [key, value] of Object.entries(info.extendedFields.items)) {
                            if (!internalAppFields.includes(key)) {
                                let label = fieldDisplayNames.get(key);

                                if (!label && key.startsWith('custom.')) {
                                    label = key.replace('custom.', '').replace(/-/g, ' ')
                                        .split(' ')
                                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                        .join(' ');
                                }

                                customFields.push({
                                    id: key,
                                    label: label || key,
                                    value: value ? String(value).trim() : '',
                                    type: 'text',
                                    fieldKey: key
                                });
                            }
                        }
                    }
                    return customFields;
                })(),

                source: 'wix',
                createdAt: contact._createdDate || new Date().toISOString(),
                updatedAt: contact._updatedDate || new Date().toISOString(),
                labels: contactLabels,
                isMember: memberStatus === 'ACTIVE' || memberStatus === 'PENDING',
                memberStatus: memberStatus,
                memberInfo: memberStatus ? { status: memberStatus } : undefined,
            };

            return contactItem;
        } catch (error) {
            if (error && typeof error === 'object' && 'code' in error && 'message' in error) {
                throw error as ApiError;
            }

            throw createApiError(
                ApiErrorCode.CONTACT_FETCH_FAILED,
                error instanceof Error ? error.message : 'Failed to fetch contact'
            );
        }
    }

    async createContact(contactData: ContactFormData): Promise<Contact> {
        console.log('🚀 DEBUG: Starting createContact with data:', contactData);
        // Create a unique key for this contact creation attempt
        const contactKey = `${contactData.firstName || ''}-${contactData.lastName || ''}-${contactData.emails?.[0]?.email || ''}-${contactData.phones?.[0]?.phone || ''}`;
        
        // Check if this contact is already being created
        if (this.creatingContacts.has(contactKey)) {
            console.log('❌ DEBUG: Contact creation already in progress for:', contactKey);
            throw createApiError(
                ApiErrorCode.CONTACT_CREATION_FAILED,
                'Contact creation already in progress'
            );
        }
        
        // Mark this contact as being created
        this.creatingContacts.add(contactKey);
        
        try {
            const primaryPhone = contactData.phones?.[0];
            const primaryAddress = contactData.addresses?.[0];
            const primaryEmail = contactData.emails?.[0];

            const extendedFieldsItems: Record<string, string> = {};

            // Handle email tags - save them as custom fields since Wix doesn't support email tags
            if (contactData.emails && contactData.emails.length > 0) {
                const allEmailTags: string[] = [];
                contactData.emails.forEach((email) => {
                    if (email.tag) {
                        allEmailTags.push(email.tag);
                    }
                });

                if (allEmailTags.length > 0) {
                    extendedFieldsItems['custom.email-tag'] = allEmailTags.join(', ');
                }
            }

            // Handle address tags - save custom ones as extended fields
            if (contactData.addresses && contactData.addresses.length > 0) {
                const allAddressTags: string[] = [];
                const wixAddressTags = ["WORK", "HOME", "BILLING", "SHIPPING"];
                
                contactData.addresses.forEach((address) => {
                    if (address.tag) {
                        const addressTypeUpper = address.tag.toUpperCase();

                        if (!wixAddressTags.includes(addressTypeUpper) && address.tag.toLowerCase() !== 'other') {
                            allAddressTags.push(address.tag);
                        }
                    }
                });

                if (allAddressTags.length > 0) {
                    extendedFieldsItems['custom.address-tag'] = allAddressTags.join(', ');
                }
            }

            let phoneTagForWix = 'MOBILE';
            if (primaryPhone?.tag) {
                const phoneTypeUpper = primaryPhone.tag.toUpperCase();
                const wixPhoneTags = ["WORK", "HOME", "MOBILE", "FAX"];

                if (wixPhoneTags.includes(phoneTypeUpper)) {
                    phoneTagForWix = phoneTypeUpper;
                } else if (primaryPhone.tag.toLowerCase() === 'other') {
                    phoneTagForWix = 'UNTAGGED';
                } else {
                    extendedFieldsItems['custom.phone-tag'] = primaryPhone.tag;
                    phoneTagForWix = 'UNTAGGED';
                }
            }

            let addressTagForWix = 'HOME';
            if (primaryAddress?.tag) {
                const addressTypeUpper = primaryAddress.tag.toUpperCase();
                const wixAddressTags = ["WORK", "HOME", "BILLING", "SHIPPING"];

                if (wixAddressTags.includes(addressTypeUpper)) {
                    addressTagForWix = addressTypeUpper;
                } else if (primaryAddress.tag.toLowerCase() === 'other') {
                    addressTagForWix = 'UNTAGGED';
                } else {
                    addressTagForWix = 'UNTAGGED';
                }
            }

            // --- FIX: Use correct Wix API structure with info wrapper per documentation ---
            const wixContact = {
                info: {
                    name: {
                        first: contactData.firstName || '',
                        last: contactData.lastName || ''
                    },
                    emails: {
                        items: [
                            {
                                email: primaryEmail?.email || '',
                                tag: 'UNTAGGED',
                                primary: true
                            },
                            ...(contactData.emails?.slice(1) || []).map(email => ({
                                email: email.email || '',
                                tag: 'UNTAGGED',
                                primary: false,
                            }))
                        ]
                    },
                    phones: {
                        items: [
                            {
                                phone: formatPhoneForWix(primaryPhone?.phone || ''),
                                tag: phoneTagForWix,
                                primary: true
                            },
                            ...(contactData.phones?.slice(1) || []).map(phone => ({
                                phone: formatPhoneForWix(phone.phone || ''),
                                tag: getPhoneTagForWix(phone.tag || ''),
                                primary: false,
                            }))
                        ]
                    },
                    addresses: {
                        items: [
                            ...(primaryAddress ? [{
                                address: {
                                    addressLine1: primaryAddress.address.addressLine1 || '',
                                    addressLine2: primaryAddress.address.addressLine2 || '',
                                    city: primaryAddress.address.city || '',
                                    subdivision: primaryAddress.address.subdivision || '',
                                    postalCode: primaryAddress.address.postalCode || '',
                                    country: getCountryCodeFromName(primaryAddress.address.country || ''),
                                },
                                tag: addressTagForWix,
                                primary: true
                            }] : []),
                            ...(contactData.addresses?.slice(1) || []).map(address => ({
                                address: {
                                    addressLine1: address.address.addressLine1 || '',
                                    addressLine2: address.address.addressLine2 || '',
                                    city: address.address.city || '',
                                    subdivision: address.address.subdivision || '',
                                    postalCode: address.address.postalCode || '',
                                    country: getCountryCodeFromName(address.address.country || ''),
                                },
                                tag: getAddressTagForWix(address.tag || ''),
                                primary: false,
                            }))
                        ]
                    },
                    company: contactData.company || '',
                    jobTitle: contactData.jobTitle || contactData.position || '',
                    birthdate: contactData.birthdate || undefined,
                }
            };

            if (contactData.customFields && contactData.customFields.length > 0) {
                const existingFields = await this.getAllCustomFields();
                const existingFieldKeys = new Set(existingFields.map(f => f.fieldKey));

                for (const field of contactData.customFields) {
                    if (field.label && field.value && field.value.trim()) {
                        const fieldKey = field.fieldKey || `custom.${field.label.toLowerCase().replace(/\s+/g, '-')}`;

                        if (!existingFieldKeys.has(fieldKey)) {
                            let dataType: 'TEXT' | 'NUMBER' | 'DATE' | 'URL' = 'TEXT';
                            switch (field.type) {
                                case 'number':
                                    dataType = 'NUMBER';
                                    break;
                                case 'date':
                                    dataType = 'DATE';
                                    break;
                                case 'url':
                                    dataType = 'URL';
                                    break;
                                default:
                                    dataType = 'TEXT';
                            }

                            await this.ensureExtendedFieldExists(fieldKey, field.label, dataType);
                        }

                        extendedFieldsItems[fieldKey] = field.value.trim();
                    }
                }
            }

            // Pass the info object as data (Firebase function expects { data: info })
            const createdContact = await callAdminApi('createContact', 'contacts', { data: wixContact.info as Record<string, unknown> });
            
            console.log('🔍 DEBUG: Created contact response:', createdContact);
            console.log('🔍 DEBUG: Available keys:', Object.keys(createdContact || {}));
            console.log('🔍 DEBUG: createdContact._id:', createdContact?._id);
            console.log('🔍 DEBUG: createdContact.id:', createdContact?.id);
            console.log('🔍 DEBUG: createdContact.contact._id:', (createdContact as Record<string, unknown>)?.contact);

            // Get the actual contact from the response
            const actualContact = (createdContact as Record<string, unknown>).contact || createdContact;

            if (Object.keys(extendedFieldsItems).length > 0) {
                // Handle both old and new Wix API response formats
                let contactId = '';
                let revision = 1;
                const idField = (actualContact as Record<string, unknown>)._id;
                
                if (typeof idField === 'string') {
                    contactId = idField;
                    // Extract revision from the contact object
                    revision = Number((actualContact as Record<string, unknown>).revision) || 1;
                } else if (typeof idField === 'object' && idField !== null) {
                    // New format: _id is an object, look for actual ID
                    contactId = (idField as Record<string, unknown>).id as string || 
                              (idField as Record<string, unknown>)._id as string ||
                              String(idField);
                    // Extract revision from the _id object or the main contact
                    revision = Number((idField as Record<string, unknown>).revision) || 
                             Number((actualContact as Record<string, unknown>).revision) || 1;
                } else {
                    contactId = (actualContact as Record<string, unknown>).id as string || '';
                    revision = Number((actualContact as Record<string, unknown>).revision) || 1;
                }
                
                await callAdminApi('updateContact', 'contacts', {
                    contactId: contactId,
                    revision: revision,
                    info: {
                        extendedFields: {
                            items: extendedFieldsItems
                        }
                    }
                });
            }

            const newContact = {
                ...contactData,
                id: typeof (actualContact as Record<string, unknown>)._id === 'object' ? String((actualContact as Record<string, unknown>)._id) : ((actualContact as Record<string, unknown>)._id as string) || '',
                _id: typeof (actualContact as Record<string, unknown>)._id === 'object' ? String((actualContact as Record<string, unknown>)._id) : ((actualContact as Record<string, unknown>)._id as string) || '',
                displayName: `${contactData.firstName || ''} ${contactData.lastName || ''}`.trim() || 
                    (contactData.emails?.[0]?.email ? contactData.emails[0].email.split('@')[0] : 'Unknown Contact'),
                primaryEmail: contactData.emails?.[0]?.email || '',
                primaryPhone: contactData.phones?.[0]?.phone || '',
                labelIds: contactData.labels || [],
                address: '',
                addressLine1: '',
                source: 'wix',
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            };

            // Fetch the complete contact data from API to ensure proper structure
            const completeContact = await this.getContactDetails(newContact.id);
            const contactToCache = completeContact || newContact;

            // Update localStorage cache to include the new contact
            const cachedContacts = localStorage.getItem('cachedContacts');
            if (cachedContacts) {
                const contacts = JSON.parse(cachedContacts);
                localStorage.setItem('cachedContacts', JSON.stringify([...contacts, contactToCache]));
            } else {
                // If no cache exists, create one with just this contact
                localStorage.setItem('cachedContacts', JSON.stringify([contactToCache]));
            }

            // Also cache the individual contact for the detail view
            localStorage.setItem(`contact-${contactToCache.id}`, JSON.stringify(contactToCache));

            return completeContact || newContact;
        } catch (error: unknown) {
            // Handle duplicate contact error specifically
            if (error instanceof Error && error.message && error.message.includes('A contact with the same primary email or phone already exists')) {
                throw createApiError(
                    ApiErrorCode.CONTACT_CREATION_FAILED,
                    `Contact already exists with this email or phone number`
                );
            }
            
            throw createApiError(
                ApiErrorCode.CONTACT_CREATION_FAILED,
                error instanceof Error ? error.message : 'Failed to create contact'
            );
        } finally {
            // Always remove the contact from the creating set, regardless of success or failure
            this.creatingContacts.delete(contactKey);
        }
    }

    private async ensureExtendedFieldExists(fieldKey: string, displayName: string, dataType: 'TEXT' | 'NUMBER' | 'DATE' | 'URL' = 'TEXT'): Promise<boolean> {
        try {
            try {
                const existingFields = await callAdminApi('queryExtendedFields', 'contacts', {
                    filter: {
                        key: { $eq: fieldKey }
                    },
                    limit: 1
                });

                if (existingFields._items && existingFields._items.length > 0) {
                    return true;
                }
            } catch (error) {
                throw createApiError(
                    ApiErrorCode.CONTACT_FETCH_FAILED,
                    `Could not check existing field ${fieldKey}: ${error instanceof Error ? error.message : 'Unknown error'}`
                );
            }


            // Use the findOrCreateExtendedField method as per Wix API documentation
            await callAdminApi('findOrCreateExtendedField', 'extendedFields', {
                displayName: displayName,
                dataType: dataType
            });

            return true;
        } catch (error) {
            if (error instanceof Error && error.message.includes('Field with same name')) {
                return true;
            }

            throw createApiError(
                ApiErrorCode.CONTACT_CREATION_FAILED,
                `Failed to create extended field ${fieldKey}: ${error instanceof Error ? error.message : String(error)}`
            );
        }
    }

    async updateContact(contactData: Contact): Promise<Contact> {
        try {
            // Add null check for contactData
            if (!contactData) {
                throw createApiError(
                    ApiErrorCode.CONTACT_UPDATE_FAILED,
                    'contactData is required for update operation'
                );
            }

            if (contactData.customFields && contactData.customFields.length > 0) {
                const existingFieldsResponse = await getCachedExtendedFields();
                const existingFieldKeys = new Set(
                    (existingFieldsResponse._items || []).map((field: unknown) => (field as { key?: string }).key)
                );

                const fieldsToCreate = contactData.customFields
                    .filter(field => field.label && field.value && field.value.trim())
                    .map(field => ({
                        fieldKey: field.fieldKey || `custom.${field.label.toLowerCase().replace(/\s+/g, '-')}`,
                        displayName: field.label
                    }))
                    .filter(field => !existingFieldKeys.has(field.fieldKey));

                if (fieldsToCreate.length > 0) {
                    await Promise.all(
                        fieldsToCreate.map(field =>
                            this.ensureExtendedFieldExists(field.fieldKey, field.displayName)
                        )
                    );
                }
            }

            let retryCount = 0;
            const maxRetries = 5;

            while (retryCount < maxRetries) {
                try {
                    const response = await callAdminApi('queryContacts', 'contacts', {
                        filter: { _id: { $eq: contactData.id } },
                        limit: 1
                    });

                    if (!response._items || response._items.length === 0) {
                        throw new Error(`Contact with ID ${contactData.id} not found`);
                    }

                    const existingContact = response._items[0] as ContactResponse & {
                        revision?: number;
                    };

                    const extendedFieldsItems: { [key: string]: string } = {};

                    if (contactData.customFields && contactData.customFields.length > 0) {
                        contactData.customFields.forEach(field => {
                            if (field.fieldKey) {
                                const fieldKey = field.fieldKey;
                                extendedFieldsItems[fieldKey] = field.value ? field.value.trim() : '';
                            }
                        });
                    }

                    if (existingContact.info?.extendedFields?.items) {
                        const existingExtendedFields = existingContact.info.extendedFields.items;

                        const internalAppFields = [
                            'custom.email-tag', 'custom.phone-tag', 'custom.address-tag'
                        ];

                        Object.keys(existingExtendedFields).forEach(fieldKey => {
                            if (fieldKey.startsWith('custom.') && !internalAppFields.includes(fieldKey)) {
                                const isInUpdateData = contactData.customFields?.some(updateField => {
                                    return updateField.fieldKey === fieldKey;
                                });

                                if (!isInUpdateData) {
                                    extendedFieldsItems[fieldKey] = '';
                                }
                            }
                        });
                    }

                    const primaryPhone = contactData.phones?.[0];
                    const primaryAddress = contactData.addresses?.[0];

                    if (contactData.emails && contactData.emails.length > 0) {
                        const allEmailTags: string[] = [];
                        contactData.emails.forEach((email) => {
                            if (email.tag) {
                                allEmailTags.push(email.tag);
                            }
                        });

                        if (allEmailTags.length > 0) {
                            extendedFieldsItems['custom.email-tag'] = allEmailTags.join(', ');
                        } else {
                            extendedFieldsItems['custom.email-tag'] = '';
                        }
                    }

                    const allPhoneTags: string[] = [];
                    if (contactData.phones && contactData.phones.length > 0) {
                        extendedFieldsItems['custom.phone-tag'] = '';

                        const wixPhoneTags = ["WORK", "HOME", "MOBILE", "FAX"];
                        contactData.phones.forEach((phone) => {
                            if (phone.tag) {
                                const phoneTypeUpper = phone.tag.toUpperCase();

                                if (!wixPhoneTags.includes(phoneTypeUpper) && phone.tag.toLowerCase() !== 'other') {
                                    allPhoneTags.push(phone.tag);
                                }
                            }
                        });

                        if (allPhoneTags.length > 0) {
                            extendedFieldsItems['custom.phone-tag'] = allPhoneTags.join(', ');
                        }
                    }

                    const allAddressTags: string[] = [];
                    if (contactData.addresses && contactData.addresses.length > 0) {
                        extendedFieldsItems['custom.address-tag'] = '';

                        const wixAddressTags = ["WORK", "HOME", "BILLING", "SHIPPING"];
                        contactData.addresses.forEach((address) => {
                            if (address.tag) {
                                const addressTypeUpper = address.tag.toUpperCase();

                                if (!wixAddressTags.includes(addressTypeUpper) && address.tag.toLowerCase() !== 'other') {
                                    allAddressTags.push(address.tag);
                                }
                            }
                        });

                        if (allAddressTags.length > 0) {
                            extendedFieldsItems['custom.address-tag'] = allAddressTags.join(', ');
                        }
                    }

                    let phoneTagForWix = 'MOBILE';
                    if (primaryPhone?.tag) {
                        const phoneTypeUpper = primaryPhone.tag.toUpperCase();
                        const wixPhoneTags = ["WORK", "HOME", "MOBILE", "FAX"];

                        if (wixPhoneTags.includes(phoneTypeUpper)) {
                            phoneTagForWix = phoneTypeUpper;
                        } else if (primaryPhone.tag.toLowerCase() === 'other') {
                            phoneTagForWix = 'UNTAGGED';
                        } else {
                            phoneTagForWix = 'UNTAGGED';
                        }
                    }

                    let addressTagForWix = 'HOME';
                    if (primaryAddress?.tag) {
                        const addressTypeUpper = primaryAddress.tag.toUpperCase();
                        const wixAddressTags = ["WORK", "HOME", "BILLING", "SHIPPING"];

                        if (wixAddressTags.includes(addressTypeUpper)) {
                            addressTagForWix = addressTypeUpper;
                        } else if (primaryAddress.tag.toLowerCase() === 'other') {
                            addressTagForWix = 'UNTAGGED';
                        } else {
                            addressTagForWix = 'UNTAGGED';
                        }
                    }

                    const info = {
                        name: {
                            first: contactData.firstName || '',
                            last: contactData.lastName || '',
                        },
                        emails: {
                            items: contactData.emails?.map((email, index) => ({
                                _id: email._id || email.id || undefined,
                                email: email.email || '',
                                tag: 'UNTAGGED',
                                primary: index === 0,
                            })) || []
                        },
                        phones: {
                            items: contactData.phones?.map((phone, index) => ({
                                _id: phone._id || phone.id || undefined,
                                phone: formatPhoneForWix(phone.phone || ''),
                                tag: index === 0 ? phoneTagForWix : getPhoneTagForWix(phone.tag || ''),
                                primary: index === 0,
                            })) || []
                        },
                        addresses: {
                            items: contactData.addresses?.map((address, index) => ({
                                _id: address._id || address.id || undefined,
                                address: {
                                    addressLine1: address.address.addressLine1 || '',
                                    addressLine2: address.address.addressLine2 || '',
                                    city: address.address.city || '',
                                    subdivision: formatSubdivisionForWix(address.address.subdivision || ''),
                                    postalCode: address.address.postalCode || '',
                                    country: getCountryCodeFromName(address.address.country || ''),
                                },
                                tag: index === 0 ? addressTagForWix : getAddressTagForWix(address.tag || ''),
                                primary: index === 0,
                            })) || []
                        },
                        company: contactData.company || '',
                        jobTitle: contactData.jobTitle || contactData.position || '',
                        birthdate: contactData.birthdate || undefined,
                        extendedFields: {
                            items: extendedFieldsItems
                        }
                    };

                    const updateParams = {
                        contactId: contactData.id,
                        info: info,
                        revision: existingContact.revision
                    };

                    await callAdminApi('updateContact', 'contacts', updateParams);
                    return contactData;

                } catch (error) {
                    if (error instanceof Error &&
                        (error.message.includes('CONTACT_ALREADY_CHANGED') ||
                            error.message.includes('revision')) &&
                        retryCount < maxRetries - 1) {

                        retryCount++;
                        // Exponential backoff
                        await new Promise(resolve => setTimeout(resolve, 200 * Math.pow(2, retryCount)));
                        continue;
                    }
                    throw error;
                }
            }

            throw new Error('Failed to update contact after maximum retries');
        } catch (error) {
            throw createApiError(
                ApiErrorCode.CONTACT_UPDATE_FAILED,
                error instanceof Error ? error.message : 'Failed to update contact'
            );
        }
    }

    async uploadContactAvatar(contactId: string, base64Data: string, mimeType?: string): Promise<{ fileUrl: string; uploadToken?: string }> {
        try {
            // Call backend to upload avatar to Wix Media ContactAvatars folder
            const result = await callAdminApi('uploadContactAvatar', 'contacts', {
                contactId,
                base64Data,
                mimeType: mimeType || 'image/jpeg'
            }) as { fileUrl: string; uploadToken?: string };

            return {
                fileUrl: result.fileUrl,
                uploadToken: result.uploadToken
            };
        } catch (error) {
            throw createApiError(
                ApiErrorCode.CONTACT_UPDATE_FAILED,
                error instanceof Error ? error.message : 'Failed to upload contact avatar'
            );
        }
    }

    async deleteContact(id: string): Promise<boolean> {
        try {
            await callAdminApi('deleteContact', 'contacts', { id: id });

            // Update localStorage cache to remove the deleted contact
            const cachedContacts = localStorage.getItem('cachedContacts');
            if (cachedContacts) {
                const contacts = JSON.parse(cachedContacts);
                const updatedContacts = contacts.filter((c: { id?: string; _id?: string }) => c.id !== id && c._id !== id);
                localStorage.setItem('cachedContacts', JSON.stringify(updatedContacts));
            }

            // Also remove the individual contact cache
            localStorage.removeItem(`contact-${id}`);
            
            return true;
        } catch (error) {
            throw createApiError(
                ApiErrorCode.CONTACT_DELETION_FAILED,
                error instanceof Error ? error.message : `Failed to delete contact ${id}`
            );
        }
    }

    async searchContacts(query: string): Promise<Contact[]> {
        try {
            const response = await callAdminApi('queryContacts', 'contacts', {
                filter: {
                    $or: [
                        { 'info.emails.email': { $in: [query] } },
                        { 'info.name.first': { $in: [query] } },
                        { 'info.name.last': { $in: [query] } },
                        { 'info.phones.phone': { $in: [query] } }
                    ]
                }
            });

            console.log(`📦 searchContacts response:`, response);

            // Response has _items, not items (Wix API returns _items structure)
            // Convert to plain array to avoid issues with Wix collection objects
            const itemsRaw = (response as Record<string, unknown>)._items || (response as Record<string, unknown>).items || [];
            const items = Array.isArray(itemsRaw) ? itemsRaw : Array.from(itemsRaw as ArrayLike<unknown>);
            
            if (!Array.isArray(items)) {
                console.warn(`❌ Invalid response structure, items not array:`, items);
                throw createApiError(
                    ApiErrorCode.CONTACT_FETCH_FAILED,
                    'Invalid response from search contacts'
                );
            }
            
            console.log(`✅ Found ${items.length} contacts matching "${query}"`);
            
            return items.map((contact: unknown) => {
                const contactResponse = contact as ContactResponse;
                
                // Helper to convert Wix collection objects to plain arrays
                const toArray = (value: unknown): unknown[] => {
                    if (Array.isArray(value)) return value;
                    if (value && typeof value === 'object' && Symbol.iterator in (value as object)) {
                        try {
                            return Array.from(value as Iterable<unknown>);
                        } catch {
                            return [];
                        }
                    }
                    return [];
                };
                
                const phonesArray = toArray(contactResponse.info?.phones || []);
                const emailsArray = toArray(contactResponse.info?.emails || []);
                
                return {
                    id: contactResponse._id || '',
                    _id: contactResponse._id || '',
                    firstName: contactResponse.info?.name?.first || '',
                    lastName: contactResponse.info?.name?.last || '',
                    avatar: contactResponse.picture || '',
                    phones: phonesArray.map((p: unknown, index: number) => {
                        const phone = p as ContactPhone;
                        return {
                            id: phone._id ? `${phone._id}-${index}` : phone.id ? `${phone.id}-${index}` : `phone-${index}-${this.generateUniqueId()}`,
                            phone: phone.phone || '',
                            tag: phone.tag || '',
                            primary: Boolean(phone.primary) || false
                        };
                    }),
                    emails: emailsArray.map((e: unknown, index: number) => {
                        const email = e as ContactEmail;
                        return {
                            id: email._id ? `${email._id}-${index}` : email.id ? `${email.id}-${index}` : `email-${index}-${this.generateUniqueId()}`,
                            email: email.email || '',
                            tag: email.tag || '',
                            isPrimary: Boolean(email.primary) || false
                        };
                    }),
                    addresses: [],
                    company: contactResponse.info?.company || '',
                    position: contactResponse.info?.position || '',
                    customFields: [],
                    source: 'wix',
                    createdAt: contactResponse._createdDate || new Date().toISOString(),
                    updatedAt: contactResponse._updatedDate || new Date().toISOString(),
                    labels: [],
                    address: '',
                    addressLine1: '',
                };
            });
        } catch (error) {
            throw createApiError(
                ApiErrorCode.CONTACT_FETCH_FAILED,
                `Error searching contacts: ${error instanceof Error ? error.message : String(error)}`
            );
        }
    }

    async importContacts(contacts: ContactFormData[]): Promise<Contact[]> {
        const importedContacts: Contact[] = [];

        try {
            for (const contactData of contacts) {
                try {
                    const wixContact = {
                        info: {
                            name: {
                                first: contactData.firstName || '',
                                last: contactData.lastName || ''
                            },
                            emails: [{
                                email: contactData.emails?.[0]?.email || '',
                                tag: contactData.emails?.[0]?.tag || '',
                                primary: true
                            }],
                            phones: [{
                                phone: contactData.phones?.[0]?.phone || '',
                                tag: contactData.phones?.[0]?.tag || '',
                                primary: true
                            }],
                            addresses: contactData.addresses?.[0] ? [{
                                address: {
                                    addressLine1: contactData.addresses[0].address.addressLine1 || '',
                                    addressLine2: contactData.addresses[0].address.addressLine2 || '',
                                    city: contactData.addresses[0].address.city || '',
                                    subdivision: contactData.addresses[0].address.subdivision || '',
                                    postalCode: contactData.addresses[0].address.postalCode || '',
                                    country: getCountryCodeFromName(contactData.addresses[0].address.country || ''),
                                },
                                tag: contactData.addresses[0].tag || '',
                                primary: true
                            }] : [],
                            company: contactData.company || '',
                            jobTitle: contactData.jobTitle || contactData.position || '',
                        }
                    };

                    const createdContact = await callAdminApi('createContact', 'contacts', { data: wixContact });

                    const contactId = createdContact._id ?
                        (typeof createdContact._id === 'object' ?
                            String(createdContact._id.toString()) :
                            String(createdContact._id)) :
                        '';

                    importedContacts.push({
                        id: contactId,
                        _id: contactId,
                        firstName: contactData.firstName || '',
                        lastName: contactData.lastName || '',
                        company: contactData.company || '',
                        position: contactData.position || '',
                        phones: contactData.phones || [],
                        emails: contactData.emails || [],
                        addresses: contactData.addresses || [],
                        address: '',
                        addressLine1: '',
                        customFields: contactData.customFields || [],
                        source: 'wix',
                        avatar: '',
                        createdAt: createdContact._createdDate && typeof createdContact._createdDate === 'string' ?
                            new Date(createdContact._createdDate).toISOString() :
                            new Date().toISOString(),
                        updatedAt: createdContact._updatedDate && typeof createdContact._updatedDate === 'string' ?
                            new Date(createdContact._updatedDate).toISOString() :
                            new Date().toISOString(),
                        labels: [],
                        notes: []
                    });
                } catch (error) {
                    throw createApiError(
                        ApiErrorCode.CONTACT_CREATION_FAILED,
                        `Error importing individual contact: ${error instanceof Error ? error.message : String(error)}`
                    );
                }
            }

            return importedContacts;
        } catch (error) {
            throw createApiError(
                ApiErrorCode.CONTACT_CREATION_FAILED,
                `Error in bulk import: ${error instanceof Error ? error.message : String(error)}`
            );
        }

    }

    async deleteExtendedField(fieldKey: string): Promise<boolean> {
        try {
            await callAdminApi('deleteExtendedField', 'contacts', {
                fieldKey: fieldKey
            });

            return true;
        } catch (error) {
            throw createApiError(
                ApiErrorCode.CONTACT_UPDATE_FAILED,
                `Failed to delete extended field ${fieldKey}: ${error instanceof Error ? error.message : String(error)}`
            );
        }
    }

    async renameExtendedField(fieldKey: string, newDisplayName: string): Promise<boolean> {
        try {
            await callAdminApi('renameExtendedField', 'contacts', {
                fieldKey: fieldKey,
                field: {
                    displayName: newDisplayName
                }
            });

            return true;
        } catch (error) {
            throw createApiError(
                ApiErrorCode.CONTACT_UPDATE_FAILED,
                `Failed to rename extended field ${fieldKey}: ${error instanceof Error ? error.message : String(error)}`
            );
        }
    }

    private async getAllCustomFieldsForLabel(fieldKey: string): Promise<string> {
        try {
            const allFields = await this.getAllCustomFields();
            const field = allFields.find(f => f.fieldKey === fieldKey);
            return field ? field.label : fieldKey.replace('custom.', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
        } catch {
            return fieldKey.replace('custom.', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
        }
    }

    async createExtendedField(fieldKey: string, displayName: string): Promise<boolean> {
        return this.ensureExtendedFieldExists(fieldKey, displayName);
    }

    clearExtendedFieldsCache(): void {
        globalExtendedFieldsCache = null;
    }

    async getEmailSubscriptionStatus(email: string): Promise<boolean> {
        try {
            const subscriptionData = await getCachedSubscriptionData([email], []);
            return subscriptionData.emailMap.get(email) || false;
        } catch (error) {
            throw createApiError(
                ApiErrorCode.CONTACT_FETCH_FAILED,
                `Error checking email subscription status: ${error instanceof Error ? error.message : String(error)}`
            );
        }
    }

    async getSMSSubscriptionStatus(phone: string): Promise<boolean> {
        try {
            const subscriptionData = await getCachedSubscriptionData([], [phone]);
            return subscriptionData.smsMap.get(phone) || false;
        } catch (error) {
            throw createApiError(
                ApiErrorCode.CONTACT_FETCH_FAILED,
                `Error checking SMS subscription status: ${error instanceof Error ? error.message : String(error)}`
            );
        }
    }

    async getBulkSubscriptionStatuses(emails: string[], phones: string[]): Promise<{
        emailSubscriptions: Map<string, boolean>;
        smsSubscriptions: Map<string, boolean>;
    }> {
        try {
            const subscriptionData = await getCachedSubscriptionData(emails, phones);
            return {
                emailSubscriptions: subscriptionData.emailMap,
                smsSubscriptions: subscriptionData.smsMap
            };
        } catch (error) {
            throw createApiError(
                ApiErrorCode.CONTACT_FETCH_FAILED,
                `Error checking bulk subscription statuses: ${error instanceof Error ? error.message : String(error)}`
            );
        }
    }

    async getAllSubscriptionData(): Promise<{
        emailSubscribers: string[];
        smsSubscribers: string[];
    }> {
        try {
            const allContacts = await this.getAllContacts();

            const allEmails: string[] = [];
            const allPhones: string[] = [];

            allContacts.forEach(contact => {
                if (contact.primaryEmail) {
                    allEmails.push(contact.primaryEmail);
                }
                if (contact.primaryPhone) {
                    allPhones.push(contact.primaryPhone);
                }
            });

            const subscriptionData = await getCachedSubscriptionData(allEmails, allPhones);

            const emailSubscribers = Array.from(subscriptionData.emailMap.entries())
                .filter(([_, isSubscribed]) => isSubscribed)
                .map(([email, _]) => email);

            const smsSubscribers = Array.from(subscriptionData.smsMap.entries())
                .filter(([_, isSubscribed]) => isSubscribed)
                .map(([phone, _]) => phone);

            return {
                emailSubscribers,
                smsSubscribers
            };
        } catch (error) {
            throw createApiError(
                ApiErrorCode.CONTACT_FETCH_FAILED,
                `Error getting all subscription data: ${error instanceof Error ? error.message : String(error)}`
            );
        }
    }



}

export interface GetFilteredContactsParams {
    contacts: ContactListItem[];
    searchText: string;
    activeFilter: string;
    selectedLabelId: string;
}

export function getFilteredContactsHelper({
    contacts,
    searchText,
    activeFilter,
    selectedLabelId,
}: GetFilteredContactsParams): ContactListItem[] {
    let result: ContactListItem[] = [];
    if (contacts.length) {
        result = contacts.filter((contact) => {
            if (!searchText) return true;
            const fullName = `${contact?.firstName || ''} ${contact?.lastName || ''}`.toLowerCase();
            const email = contact?.primaryEmail?.toLowerCase() || '';
            const phone = contact?.primaryPhone?.toLowerCase() || '';
            const searchTermLower = searchText?.toLowerCase() || '';
            return (
                fullName.includes(searchTermLower) ||
                email.includes(searchTermLower) ||
                phone.includes(searchTermLower)
            );
        });
        result = [...result].sort((a, b) => {
            const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.primaryEmail || '';
            const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim() || b.primaryEmail || '';
            return nameA.localeCompare(nameB);
        });
        if (activeFilter === 'recent') {
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
            result = result.filter((contact) => {
                if (!contact.createdAt) return false;
                const creationDate = new Date(contact.createdAt);
                return !isNaN(creationDate.getTime()) && creationDate > thirtyDaysAgo;
            });
        }
        if (activeFilter === 'email-subscribers') {
            result = result.filter((contact) => contact.emailSubscribed === true);
        }
        if (activeFilter === 'sms-subscribers') {
            result = result.filter((contact) => contact.smsSubscribed === true);
        }
        if (
            activeFilter !== 'all' &&
            activeFilter !== 'recent' &&
            activeFilter !== 'label' &&
            activeFilter !== 'email-subscribers' &&
            activeFilter !== 'sms-subscribers'
        ) {
            result = result.filter((contact) => {
                const { clientType } = contact.contactfilter?.[0] || {};
                if (Array.isArray(clientType)) {
                    return clientType.includes(activeFilter);
                }
                return clientType === activeFilter;
            });
        }
        if (activeFilter === 'label' && selectedLabelId) {
            result = result.filter((contact) => contact.labels?.includes(selectedLabelId));
        }
    }
    return result;
}

// Contact update event listeners
type ContactUpdateListener = (event: { contactId: string; phone?: string }) => void;
const contactUpdateListeners = new Set<ContactUpdateListener>();

/**
 * Subscribe to contact update events
 * Called when a contact is created or updated
 */
export function onContactUpdate(listener: ContactUpdateListener): () => void {
  contactUpdateListeners.add(listener);
  // Return unsubscribe function
  return () => {
    contactUpdateListeners.delete(listener);
  };
}

/**
 * Notify all listeners of a contact update
 * Used internally when contacts are created or modified
 */
export function notifyContactUpdate(contactId: string, phone?: string): void {
  contactUpdateListeners.forEach(listener => {
    try {
      listener({ contactId, phone });
    } catch (error) {
      console.error('Error in contact update listener:', error);
    }
  });
}
